import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiMenu,
  FiX,
  FiUser,
  FiLogOut,
  FiHome,
  FiGrid3X3,
  FiCalendar,
  FiSettings,
  FiChevronDown,
  FiInfo,
  FiPhone,
} = FiIcons;

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const servicesRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setIsServicesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
    setIsProfileOpen(false);
  };

  const guestNavItems = [
    { path: '/', label: 'Home', icon: FiHome },
    { path: '/services', label: 'Services', icon: FiGrid3X3 },
    { path: '/about', label: 'About', icon: FiInfo },
    { path: '/contact', label: 'Contact', icon: FiPhone },
  ];

  const authNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: FiGrid3X3 },
    { path: '/scheduling', label: 'Schedule', icon: FiCalendar },
  ];

  const navItems = user ? authNavItems : guestNavItems;

  const servicesList = [
    { path: '/property-care', label: 'Property Care' },
    { path: '/parental-care', label: 'Parental Care' },
    { path: '/travel-services', label: 'Travel Services' },
    { path: '/celebrate', label: 'Celebrate' },
    { path: '/financial-advisory', label: 'Financial Advisory' },
  ];

  return (
    <header className="bg-white sticky top-0 z-50 border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Adjusted header height to h-16 on mobile and h-20 on desktop */}
        <div className="flex justify-between items-center h-20 md:h-24 transition-all duration-300">

          {/* Logo - Left Corner with dynamic responsive sizing */}
          <Link
            to="/"
            className="flex items-center flex-shrink-0"
          >
            <img
              src="/images/NRI_Logo.svg"
              alt="NRI Local Buddy Logo"
              className="h-10 sm:h-14 md:h-16 w-auto object-contain transition-all duration-300"
            />
          </Link>

          {/* Center Navigation - Desktop */}
          <nav className="hidden md:flex items-center space-x-1 flex-1 justify-center">
            {navItems.map((item) => {
              if (item.label === 'Services') {
                const isServicesActive = location.pathname === '/services' || servicesList.some(s => s.path === location.pathname);
                return (
                  <div key={item.path} className="relative" ref={servicesRef}>
                    <button
                      onClick={() => setIsServicesOpen(!isServicesOpen)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center space-x-1 outline-none ${
                        isServicesActive
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                      }`}
                    >
                      <span>{item.label}</span>
                      <FiChevronDown className={`w-4 h-4 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {isServicesOpen && (
                      <div className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                        <Link
                          to="/services"
                          className="block px-4 py-2 text-sm font-semibold text-gray-900 border-b border-gray-100 hover:bg-gray-50"
                          onClick={() => setIsServicesOpen(false)}
                        >
                          All Services
                        </Link>
                        {servicesList.map((service) => (
                          <Link
                            key={service.path}
                            to={service.path}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors"
                            onClick={() => setIsServicesOpen(false)}
                          >
                            {service.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${isActive
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                    }`}
                >
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Right Section - Auth/Profile */}
          <div className="flex items-center space-x-3">
            {user ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center space-x-2.5 px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-all duration-200 group"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow duration-200 ring-2 ring-white">
                    <FiUser className="text-white text-base" />
                  </div>
                  <SafeIcon
                    icon={FiChevronDown}
                    className={`text-gray-400 text-sm transition-transform duration-200 hidden lg:block ${isProfileOpen ? 'rotate-180' : ''
                      }`}
                  />
                </button>

                {/* Dropdown Menu */}
                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-56 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-lg border border-gray-100 py-2 animate-in fade-in slide-in-from-top-2 duration-200 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-400 mt-1 truncate">{user.email}</p>
                    </div>

                    <Link
                      to="/profile"
                      className="flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                        <SafeIcon icon={FiUser} className="text-blue-600 text-sm" />
                      </div>
                      <div>
                        <p className="font-medium">My Profile</p>
                        <p className="text-xs text-gray-500">View and edit profile</p>
                      </div>
                    </Link>

                    <div className="border-t border-gray-100 mt-2 pt-2">
                      <button
                        onClick={handleLogout}
                        className="flex items-center space-x-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150 w-full"
                      >
                        <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
                          <SafeIcon icon={FiLogOut} className="text-red-600 text-sm" />
                        </div>
                        <div className="text-left">
                          <p className="font-medium">Log Out</p>
                          <p className="text-xs text-red-400">Sign out of account</p>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="px-3.5 sm:px-5 py-1.5 sm:py-2 bg-blue-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-sm whitespace-nowrap"
              >
                Sign In
              </Link>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-950 transition-colors"
              aria-label="Toggle menu"
            >
              <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="text-xl" />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-100 py-3 max-h-[calc(100vh-5rem)] overflow-y-auto">
            <nav className="space-y-1">
              {navItems.map((item) => {
                if (item.label === 'Services') {
                  const isServicesActive = location.pathname === '/services' || servicesList.some(s => s.path === location.pathname);
                  return (
                    <div key={item.path} className="space-y-1">
                      <button
                        onClick={() => setIsServicesOpen(!isServicesOpen)}
                        className={`flex items-center justify-between w-full px-3 py-2.5 rounded-lg transition-colors outline-none ${
                          isServicesActive
                            ? 'text-blue-600 bg-blue-50 font-semibold'
                            : 'text-gray-700 hover:bg-gray-50 font-medium'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          {item.icon && <SafeIcon icon={item.icon} className="text-lg" />}
                          <span>{item.label}</span>
                        </div>
                        <FiChevronDown className={`w-4 h-4 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isServicesOpen && (
                        <div className="pl-8 space-y-1 bg-gray-50/50 rounded-lg py-1">
                          <Link
                            to="/services"
                            className="block px-3 py-2 text-sm text-gray-600 hover:text-blue-600 font-medium"
                            onClick={() => { setIsMenuOpen(false); setIsServicesOpen(false); }}
                          >
                            All Services
                          </Link>
                          {servicesList.map((service) => (
                            <Link
                              key={service.path}
                              to={service.path}
                              className="block px-3 py-2 text-sm text-gray-600 hover:text-blue-600 font-medium"
                              onClick={() => { setIsMenuOpen(false); setIsServicesOpen(false); }}
                            >
                              {service.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-colors ${isActive
                      ? 'text-blue-600 bg-blue-50 font-semibold'
                      : 'text-gray-700 hover:bg-gray-50 font-medium'
                      }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.icon && <SafeIcon icon={item.icon} className="text-lg" />}
                    <span>{item.label}</span>
                  </Link>
                );
              })}

              {user && (
                <>
                  <div className="border-t border-gray-100 my-2 pt-2">
                    <Link
                      to="/profile"
                      className="flex items-center space-x-3 px-3 py-2.5 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors font-medium"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <SafeIcon icon={FiUser} className="text-lg" />
                      <span>My Profile</span>
                    </Link>
                    <Link
                      to="/settings"
                      className="flex items-center space-x-3 px-3 py-2.5 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors font-medium"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <SafeIcon icon={FiSettings} className="text-lg" />
                      <span>Settings</span>
                    </Link>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-3 px-3 py-2.5 text-red-600 hover:bg-red-50 rounded-lg w-full text-left transition-colors font-medium"
                  >
                    <SafeIcon icon={FiLogOut} className="text-lg" />
                    <span>Log Out</span>
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;